import React, { useContext, useEffect, useRef, useState } from "react";
import MenuButton from "../MenuButton/MenuButton";
import UserServices from "../../UserServices/UserServices";
import Pagination from "react-js-pagination";
import { NoteState } from "../../context/notes/NoteState";
import { Modal, Button } from "react-bootstrap";
import moment from "moment";
import { Tooltip } from "react-tooltip";
import jsPDF from "jspdf";
import "jspdf-autotable";

// Generate 24 one-hour time-slot options
const TIME_SLOTS = Array.from({ length: 24 }, (_, h) => {
  const start = moment({ hour: h });
  const end   = moment({ hour: (h + 1) % 24 });
  return {
    value: start.format("HH:mm"),  // stored as appointment_time start
    label: `${start.format("hh:mm A")} - ${end.format("hh:mm A")}`,
  };
});

const SPECIAL_TRAINER_IDS = {
  G13_BRANCH: "special:g13_branch",
  OUTSIDER: "special:outsider",
  GENERAL_TRAINER: "special:general_trainer",
};

const formatTrainerOptionLabel = (trainer) => {
  if (trainer?.is_special) {
    return trainer.first_name;
  }
  return `${trainer.first_name || ""} ${trainer.last_name || ""}`.trim();
};

const resolveTrainerSelectValue = (appointment) => {
  if (appointment?.trainer_label === "G13 Branch") {
    return SPECIAL_TRAINER_IDS.G13_BRANCH;
  }
  if (appointment?.trainer_label === "Outsider") {
    return SPECIAL_TRAINER_IDS.OUTSIDER;
  }
  if (appointment?.trainer_label === "General Trainer") {
    return SPECIAL_TRAINER_IDS.GENERAL_TRAINER;
  }
  return appointment?.trainer_id || "";
};

const formatTrainerDisplay = (appointment) => {
  if (appointment?.trainer_label) {
    return appointment.trainer_label;
  }
  if (appointment?.trainer) {
    return `${appointment.trainer.first_name} ${appointment.trainer.last_name}`.trim();
  }
  return "—";
};

const applyTrainerToSubmitPayload = (payload) => {
  const next = { ...payload };
  if (next.trainer_id === SPECIAL_TRAINER_IDS.G13_BRANCH) {
    next.trainer_id = null;
    next.trainer_label = "G13 Branch";
  } else if (next.trainer_id === SPECIAL_TRAINER_IDS.OUTSIDER) {
    next.trainer_id = null;
    next.trainer_label = "Outsider";
  } else if (next.trainer_id === SPECIAL_TRAINER_IDS.GENERAL_TRAINER) {
    next.trainer_id = null;
    next.trainer_label = "General Trainer";
  } else {
    next.trainer_label = null;
    if (!next.trainer_id) {
      next.trainer_id = null;
    }
  }
  return next;
};

const NutritionAppointments = () => {
  const [loading, setLoading] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [limit, setLimit] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);

  const [clients, setClients] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [conversionOptions, setConversionOptions] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);  // ["13:00", ...]

  const [searchTerm, setSearchTerm] = useState("");
  const [filterConversion, setFilterConversion] = useState("");
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");
  
  const [clientSearchTerm, setClientSearchTerm] = useState("");
  const [filteredClients, setFilteredClients] = useState([]);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const clientDropdownRef = useRef(null);

  const { state } = useContext(NoteState);
  const { branch_id, role, UId } = state;

  const [formData, setFormData] = useState({
    date: moment().format("YYYY-MM-DD"),
    client_id: "",
    client_name: "",
    contact: "",
    appointment_date: "",
    appointment_time: "",
    consultation: "",
    conversion: "",
    client_remarks: "",
    nutritionist_id: role === "10" ? UId : "",
    trainer_id: "",
    branch_id: branch_id,
  });

  useEffect(() => {
    fetchAppointments(currentPage, limit);
    fetchTrainers();
    fetchConversionOptions();
    fetchClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close client dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(e.target)) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Fetch booked slots when appointment_date changes in the form
  useEffect(() => {
    if (formData.appointment_date && branch_id) {
      const excludeId = editMode && selectedAppointment ? selectedAppointment.id : null;
      fetchBookedSlots(formData.appointment_date, excludeId);
    } else {
      setBookedSlots([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.appointment_date]);

  const fetchAppointments = async (page, limitNo, filters = {}) => {
    setLoading(true);
    try {
      const params = {
        nutritionist_id: role === "10" ? UId : filters.nutritionist_id,
        branch_id: branch_id,
        search: filters.search || searchTerm,
        conversion: filters.conversion || filterConversion,
        start_date: filters.start_date || filterStartDate,
        end_date: filters.end_date || filterEndDate,
      };

      const response = await UserServices.getNutritionAppointments(
        limitNo,
        page,
        params
      );

      if (response.status === 200 && response.data.status) {
        const data = response.data.data;
        setAppointments(Array.isArray(data) ? data : data.data || []);
        
        if (response.data.pagination) {
          setTotalResults(response.data.pagination.total_record || 0);
          setTotalPages(response.data.pagination.total_pages || 1);
          setCurrentPage(response.data.pagination.current_page || 1);
        }
      }
    } catch (err) {
      console.log(err);
      setAppointments([]);
    }
    setLoading(false);
  };

  const fetchClients = async () => {
    try {
      const response = await UserServices.getClientsName(branch_id);
      if (response.status === 200) {
        const results = response.data.data.map((client) => ({
          id: client.id,
          name: `${client.first_name} ${client.last_name}`,
          phone: client.phone,
          first_name: client.first_name,
          last_name: client.last_name,
        }));
        setClients(results);
        setFilteredClients(results);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const fetchTrainers = async () => {
    try {
      const response = await UserServices.getTrainersForNutrition(branch_id);
      if (response.status === 200 && response.data.status) {
        setTrainers(response.data.data);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const fetchBookedSlots = async (date, excludeId = null) => {
    try {
      const nid = role === "10" ? UId : (formData.nutritionist_id || null);
      const response = await UserServices.getNutritionBookedSlots(date, branch_id, nid, excludeId);
      if (response.status === 200 && response.data.status) {
        setBookedSlots(response.data.data || []);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const fetchConversionOptions = async () => {
    try {
      const response = await UserServices.getConversionOptions();
      if (response.status === 200 && response.data.status) {
        setConversionOptions(response.data.data);
      }
    } catch (err) {
      console.log(err);
    }
  };

  // ── API helpers ──────────────────────────────────────────────────────────

  const handleOpenModal = (appointment = null) => {
    if (appointment) {
      setEditMode(true);
      setSelectedAppointment(appointment);
      const clientName = appointment.client_name ||
        (appointment.client
          ? `${appointment.client.first_name} ${appointment.client.last_name}`
          : "");
      setFormData({
        date: appointment.date || moment().format("YYYY-MM-DD"),
        client_id: appointment.client_id || "",
        client_name: clientName,
        contact: appointment.contact || "",
        appointment_date: appointment.appointment_date || "",
        appointment_time: appointment.appointment_time
          ? appointment.appointment_time.substring(0, 5)
          : "",
        consultation: appointment.consultation || "",
        conversion: appointment.conversion || "",
        client_remarks: appointment.client_remarks || "",
        nutritionist_id: appointment.nutritionist_id || (role === "10" ? UId : ""),
        trainer_id: resolveTrainerSelectValue(appointment),
        branch_id: appointment.branch_id || branch_id,
      });
      setClientSearchTerm(clientName);
      if (appointment.appointment_date) {
        fetchBookedSlots(appointment.appointment_date, appointment.id);
      }
    } else {
      setEditMode(false);
      setSelectedAppointment(null);
      setFormData({
        date: moment().format("YYYY-MM-DD"),
        client_id: "",
        client_name: "",
        contact: "",
        appointment_date: "",
        appointment_time: "",
        consultation: "",
        conversion: "",
        client_remarks: "",
        nutritionist_id: role === "10" ? UId : "",
        trainer_id: "",
        branch_id: branch_id,
      });
      setClientSearchTerm("");
      setBookedSlots([]);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditMode(false);
    setSelectedAppointment(null);
    setClientSearchTerm("");
    setShowClientDropdown(false);
    setBookedSlots([]);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleClientSearch = (e) => {
    const v = e.target.value;
    setClientSearchTerm(v);
    setShowClientDropdown(true);
    setFilteredClients(
      v.trim() === ""
        ? clients
        : clients.filter(
            (c) =>
              c.name.toLowerCase().includes(v.toLowerCase()) ||
              (c.phone && c.phone.includes(v))
          )
    );
  };

  const handleClientSelect = (client) => {
    setFormData((prev) => ({
      ...prev,
      client_id: client.id,
      client_name: client.name,
      contact: client.phone || prev.contact,
    }));
    setClientSearchTerm(client.name);
    setShowClientDropdown(false);
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return; // prevent double submission
    setLoading(true);

    try {
      const submitData = applyTrainerToSubmitPayload({
        ...formData,
        appointment_time: formData.appointment_time
          ? formData.appointment_time.length === 5
            ? `${formData.appointment_time}:00`
            : formData.appointment_time
          : null,
      });

      let response;
      if (editMode) {
        response = await UserServices.updateNutritionAppointment(
          selectedAppointment.id,
          submitData
        );
      } else {
        response = await UserServices.createNutritionAppointment(submitData);
      }

      if (response.status === 200 || response.status === 201 || response.status === 204) {
        handleCloseModal();
        fetchAppointments(currentPage, limit);
      }
    } catch (err) {
      console.log(err);
      const errMsg = err?.response?.data?.message || err?.response?.data?.errors
        ? Object.values(err.response.data.errors || {}).flat().join("\n")
        : "Error saving appointment. Please try again.";
      alert(errMsg);
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this appointment?")) {
      try {
        const response = await UserServices.deleteNutritionAppointment(id);
        if (response.status === 200 || response.status === 204) {
          fetchAppointments(currentPage, limit);
        }
      } catch (err) {
        console.log(err);
        alert("Error deleting appointment.");
      }
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      const response = await UserServices.changeNutritionAppointmentStatus(
        id,
        status
      );
      if (response.status === 200 || response.status === 204) {
        fetchAppointments(currentPage, limit);
      }
    } catch (err) {
      console.log(err);
      alert("Error updating status.");
    }
  };

  const handleSearch = () => {
    fetchAppointments(1, limit, {
      search: searchTerm,
      conversion: filterConversion,
      start_date: filterStartDate,
      end_date: filterEndDate,
    });
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setFilterConversion("");
    setFilterStartDate("");
    setFilterEndDate("");
    fetchAppointments(1, limit, { search: "", conversion: "", start_date: "", end_date: "" });
  };

  const handleExport = async () => {
    try {
      const response = await UserServices.exportNutritionAppointments({
        nutritionist_id: role === "10" ? UId : null,
        branch_id: branch_id,
        search: searchTerm,
        conversion: filterConversion,
        start_date: filterStartDate,
        end_date: filterEndDate,
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `Nutrition_Appointments_${moment().format("YYYY-MM-DD")}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.log(err);
      alert("Error exporting appointments.");
    }
  };

  const [pdfLoading, setPdfLoading] = useState(false);

  const handlePdfExport = async () => {
    setPdfLoading(true);
    try {
      const res = await UserServices.getNutritionAppointments(null, null, {
        nutritionist_id: role === "10" ? UId : null,
        branch_id,
        search: searchTerm,
        conversion: filterConversion,
        start_date: filterStartDate,
        end_date: filterEndDate,
      });
      const allAppts = Array.isArray(res.data.data) ? res.data.data : [];
      const doc = new jsPDF("l", "mm", "a4");
      doc.setFontSize(16);
      doc.text("Nutrition Appointments Report", 14, 18);
      doc.setFontSize(10);
      doc.text(`Generated: ${moment().format("DD MMM YYYY")}`, 14, 26);
      doc.autoTable({
        startY: 32,
        head: [["S#", "Client Name", "Contact", "Appt Date", "Time", "Consultation", "Conversion", "Nutritionist"]],
        body: allAppts.map((a, i) => [
          i + 1,
          a.client_name || (a.client ? `${a.client.first_name} ${a.client.last_name}` : "N/A"),
          a.contact || "N/A",
          a.appointment_date ? moment(a.appointment_date).format("DD/MM/YYYY") : "",
          a.appointment_time ? moment(a.appointment_time, "HH:mm:ss").format("HH:mm") : "",
          a.consultation || "N/A",
          a.conversion || "N/A",
          a.nutritionist ? `${a.nutritionist.first_name} ${a.nutritionist.last_name}` : "N/A",
        ]),
        headStyles: { fillColor: [26, 92, 69], textColor: 255 },
        alternateRowStyles: { fillColor: [240, 248, 244] },
        styles: { fontSize: 8 },
      });
      doc.save(`Nutrition_Appointments_${moment().format("YYYY-MM-DD")}.pdf`);
    } catch (err) {
      console.log(err);
      alert("Error generating PDF.");
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div className="site-main-design">
      <MenuButton />
      <div className="container-fluid">
        {/* Header */}
        <div className="row">
          <div className="col-12">
            <div className="page-heading d-flex justify-content-between align-items-center">
              <h3>
                <i className="bi bi-calendar-check me-2"></i>
                Nutrition Appointments
              </h3>
              <button
                className="btn btn-primary"
                onClick={() => handleOpenModal()}
              >
                <i className="bi bi-plus-circle me-2"></i>
                Add Appointment
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="card border-0 shadow-sm mb-3">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-3">
                <label className="form-label">Search</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by name or contact..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="col-md-2">
                <label className="form-label">Conversion</label>
                <select
                  className="form-select"
                  value={filterConversion}
                  onChange={(e) => setFilterConversion(e.target.value)}
                >
                  <option value="">All</option>
                  {conversionOptions.map((option, index) => (
                    <option key={index} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label">Start Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={filterStartDate}
                  onChange={(e) => setFilterStartDate(e.target.value)}
                />
              </div>
              <div className="col-md-2">
                <label className="form-label">End Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={filterEndDate}
                  onChange={(e) => setFilterEndDate(e.target.value)}
                />
              </div>
              <div className="col-md-3 d-flex align-items-end gap-2">
                <button
                  className="btn btn-primary"
                  onClick={handleSearch}
                >
                  <i className="bi bi-search me-2"></i>
                  Search
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={handleClearFilters}
                >
                  <i className="bi bi-x-circle me-2"></i>
                  Clear
                </button>
                <button
                  className="btn btn-success"
                  onClick={handleExport}
                  disabled={pdfLoading}
                >
                  <i className="bi bi-download me-2"></i>
                  Export
                </button>
                <button
                  className="btn btn-danger"
                  onClick={handlePdfExport}
                  disabled={pdfLoading}
                >
                  <i className="bi bi-file-earmark-pdf me-2"></i>
                  {pdfLoading ? "Generating..." : "PDF"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Appointments Table */}
        <div className="card border-0 shadow-sm">
          <div className="card-body">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : appointments.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="bi bi-inbox fs-1"></i>
                <p className="mt-2">No appointments found</p>
              </div>
            ) : (
              <>
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead className="table-light">
                      <tr>
                        <th>S#</th>
                        <th>Entry Date</th>
                        <th>Client</th>
                        <th>Contact</th>
                        <th>Appt Date</th>
                        <th>Time Slot</th>
                        <th>Consultation</th>
                        <th>Conversion</th>
                        <th>Trainer</th>
                        <th>Remarks</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {appointments.map((appointment, index) => (
                        <tr key={appointment.id}>
                          <td>{(currentPage - 1) * limit + index + 1}</td>
                          <td>{moment(appointment.date).format("DD/MM/YY")}</td>
                          <td>
                            {appointment.client_name ||
                              (appointment.client
                                ? `${appointment.client.first_name} ${appointment.client.last_name}`
                                : "N/A")}
                          </td>
                          <td>{appointment.contact || "—"}</td>
                          <td>
                            {appointment.appointment_date
                              ? moment(appointment.appointment_date).format("DD/MM/YY")
                              : "—"}
                          </td>
                          <td>
                            <span className="text-nowrap">
                              {(() => {
                                if (!appointment.appointment_time) return "—";
                                // appointment_time comes as HH:mm:ss from API
                                const raw = String(appointment.appointment_time);
                                // Normalize: take first 5 chars "HH:mm"
                                const hhmm = raw.length >= 5 ? raw.substring(0, 5) : raw;
                                const slot = TIME_SLOTS.find((s) => s.value === hhmm);
                                return slot
                                  ? slot.label
                                  : moment(raw, ["HH:mm:ss", "HH:mm"]).format("hh:mm A");
                              })()}
                            </span>
                          </td>
                          <td>{appointment.consultation || "—"}</td>
                          <td>
                            <span
                              className={`badge ${
                                appointment.conversion === "Paid"
                                  ? "bg-success"
                                  : appointment.conversion === "Gold" ||
                                    appointment.conversion === "Silver" ||
                                    appointment.conversion === "Platinum"
                                  ? "bg-warning text-dark"
                                  : appointment.conversion === "Not Attended"
                                  ? "bg-danger"
                                  : "bg-info text-dark"
                              }`}
                            >
                              {appointment.conversion || "Scheduled"}
                            </span>
                          </td>
                          <td>{formatTrainerDisplay(appointment)}</td>
                          <td>
                            <span
                              style={{ maxWidth: 140, display: "inline-block", overflow: "hidden",
                                textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                              title={appointment.client_remarks}
                            >
                              {appointment.client_remarks || "—"}
                            </span>
                          </td>
                          <td>
                            <div className="btn-group btn-group-sm">
                              <button
                                className="btn btn-outline-primary"
                                onClick={() => handleOpenModal(appointment)}
                                data-tooltip-id="edit-tooltip"
                                data-tooltip-content="Edit"
                              >
                                <i className="bi bi-pencil"></i>
                              </button>
                              <button
                                className="btn btn-outline-danger"
                                onClick={() => handleDelete(appointment.id)}
                                data-tooltip-id="delete-tooltip"
                                data-tooltip-content="Delete"
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                              {appointment.status === "1" ? (
                                <button
                                  className="btn btn-outline-warning"
                                  onClick={() =>
                                    handleStatusChange(appointment.id, "0")
                                  }
                                  data-tooltip-id="deactivate-tooltip"
                                  data-tooltip-content="Deactivate"
                                >
                                  <i className="bi bi-x-circle"></i>
                                </button>
                              ) : (
                                <button
                                  className="btn btn-outline-success"
                                  onClick={() =>
                                    handleStatusChange(appointment.id, "1")
                                  }
                                  data-tooltip-id="activate-tooltip"
                                  data-tooltip-content="Activate"
                                >
                                  <i className="bi bi-check-circle"></i>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="d-flex justify-content-center mt-3">
                    <Pagination
                      activePage={currentPage}
                      itemsCountPerPage={limit}
                      totalItemsCount={totalResults}
                      pageRangeDisplayed={5}
                      onChange={(p) => { setCurrentPage(p); fetchAppointments(p, limit); }}
                      itemClass="page-item"
                      linkClass="page-link"
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Add/Edit Modal */}
        <Modal show={showModal} onHide={handleCloseModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              {editMode ? "Edit Appointment" : "Add New Appointment"}
            </Modal.Title>
          </Modal.Header>
          <form onSubmit={handleSubmit}>
            <Modal.Body>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Entry Date</label>
                  <input
                    type="date"
                    className="form-control"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Search & Select Client <small className="text-muted">(optional)</small></label>
                  <div className="position-relative" ref={clientDropdownRef}>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Type to search client by name or phone..."
                      value={clientSearchTerm}
                      onChange={handleClientSearch}
                      onFocus={() => { setShowClientDropdown(true); setFilteredClients(clients); }}
                      autoComplete="off"
                    />
                    {showClientDropdown && filteredClients.length > 0 && (
                      <div
                        className="position-absolute w-100 bg-white border rounded shadow-sm"
                        style={{
                          maxHeight: "250px",
                          overflowY: "auto",
                          zIndex: 1050,
                          top: "100%",
                          marginTop: "2px"
                        }}
                      >
                        {filteredClients.map((client) => (
                          <div
                            key={client.id}
                            className="p-2 border-bottom cursor-pointer hover-bg-light"
                            onClick={() => handleClientSelect(client)}
                            style={{ cursor: "pointer" }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f8f9fa"}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "white"}
                          >
                            <div className="fw-semibold">{client.name}</div>
                            {client.phone && (
                              <small className="text-muted">{client.phone}</small>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <small className="text-muted">Search and select existing client or enter manually below</small>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Client Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    name="client_name"
                    value={formData.client_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Contact *</label>
                  <input
                    type="text"
                    className="form-control"
                    name="contact"
                    value={formData.contact}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Appointment Date *</label>
                  <input
                    type="date"
                    className="form-control"
                    name="appointment_date"
                    value={formData.appointment_date}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">
                    Appointment Time
                    {bookedSlots.length > 0 && (
                      <small className="text-danger ms-2">
                        ({bookedSlots.length} slot{bookedSlots.length > 1 ? "s" : ""} booked)
                      </small>
                    )}
                  </label>
                  <select
                    className="form-select"
                    name="appointment_time"
                    value={formData.appointment_time}
                    onChange={handleInputChange}
                  >
                    <option value="">— Select Time Slot —</option>
                    {TIME_SLOTS.map((slot) => {
                      const isBooked = bookedSlots.includes(slot.value);
                      return (
                        <option
                          key={slot.value}
                          value={slot.value}
                          disabled={isBooked}
                          style={isBooked ? { color: "#bbb" } : {}}
                        >
                          {slot.label}{isBooked ? "  ✗ Booked" : ""}
                        </option>
                      );
                    })}
                  </select>
                  {formData.appointment_date && (
                    <small className="text-muted">Slots marked ✗ are already booked on this date</small>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Consultation</label>
                  <input
                    type="text"
                    className="form-control"
                    name="consultation"
                    value={formData.consultation}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Conversion</label>
                  <select
                    className="form-select"
                    name="conversion"
                    value={formData.conversion}
                    onChange={handleInputChange}
                  >
                    <option value="">Select Status</option>
                    {conversionOptions.map((option, index) => (
                      <option key={index} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Trainer</label>
                  <select
                    className="form-select"
                    name="trainer_id"
                    value={formData.trainer_id}
                    onChange={handleInputChange}
                  >
                    <option value="">— Select Trainer —</option>
                    {trainers.map((t) => (
                      <option key={t.id} value={t.id}>
                        {formatTrainerOptionLabel(t)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-12">
                  <label className="form-label">Client Remarks</label>
                  <textarea
                    className="form-control"
                    name="client_remarks"
                    rows="3"
                    value={formData.client_remarks}
                    onChange={handleInputChange}
                  ></textarea>
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? "Saving..." : editMode ? "Update" : "Create"}
              </Button>
            </Modal.Footer>
          </form>
        </Modal>

        {/* Tooltips */}
        <Tooltip id="edit-tooltip" />
        <Tooltip id="delete-tooltip" />
        <Tooltip id="activate-tooltip" />
        <Tooltip id="deactivate-tooltip" />
      </div>
    </div>
  );
};

export default NutritionAppointments;
